import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DashboardWrapper, Footer } from 'src/shared';
import styled from 'styled-components';

const ReasoningContainer = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
`;

const Content = styled.div`
  flex: 1;
  padding: 40px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
`;

const Header = styled.div`
  background: white;
  border-radius: 16px;
  padding: 30px;
  margin-bottom: 30px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
`;

const KNumberDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
`;

const KNumberBadge = styled.div`
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  padding: 12px 20px;
  border-radius: 25px;
  font-size: 1.1rem;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
`;

const KNumberLabel = styled.span`
  font-size: 1.2rem;
  font-weight: 600;
  color: #374151;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  color: #1f2937;
  margin-bottom: 15px;
  line-height: 1.2;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #6b7280;
  line-height: 1.6;
  max-width: 800px;
`;

const MainContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 30px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
`;

const ContentTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 20px;
`;

const ContentText = styled.p`
  font-size: 1rem;
  color: #4b5563;
  line-height: 1.7;
  margin-bottom: 15px;
`;

const Reasoning: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [kNumber, setKNumber] = useState<string>('');

  useEffect(() => {
    // Extract K number from URL params or state
    const params = new URLSearchParams(location.search);
    const kNum = params.get('k_number') || 'K123456';
    setKNumber(kNum);
  }, [location]);

  const handleBackToHome = () => {
    navigate('/');
  };

  return (
    <DashboardWrapper>
      <ReasoningContainer>
        <Content>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
            <button
              onClick={handleBackToHome}
              style={{
                backgroundColor: '#4f46e5',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              }}
            >
              Back to Home
            </button>
          </div>
          
          <Header>
            <KNumberDisplay>
              <KNumberBadge>
                {kNumber}
              </KNumberBadge>
              <KNumberLabel>
                Medical Device Analysis
              </KNumberLabel>
            </KNumberDisplay>
            
            <Title>
              AI-Powered Device Reasoning
            </Title>
            
            <Subtitle>
              Advanced analysis and insights for medical device {kNumber} using our multimodal AI reasoning engine
            </Subtitle>
          </Header>

          <MainContent>
            <ContentTitle>
              Device Analysis & Insights
            </ContentTitle>
            
            <ContentText>
              This page provides comprehensive AI-powered analysis of medical device {kNumber}. 
              Our advanced reasoning engine processes multiple data sources including:
            </ContentText>
            
            <ContentText>
              • FDA regulatory documents and safety data<br/>
              • Clinical trial results and patient outcomes<br/>
              • Technical specifications and material composition<br/>
              • Adverse event reports and post-market surveillance<br/>
              • Comparative analysis with similar devices
            </ContentText>
            
            <ContentText>
              The AI reasoning engine identifies patterns, correlations, and insights that may not be 
              immediately apparent through traditional analysis methods, helping researchers and 
              clinicians make more informed decisions.
            </ContentText>
          </MainContent>
        </Content>
        
        <Footer />
      </ReasoningContainer>
    </DashboardWrapper>
  );
};

export default Reasoning;
